import { useEffect, useState } from "react";
import { toast } from "sonner";
import PoiEditor from "./PoiEditor";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";

export default function RouteForm({
  value,
  onChange,
  statusOptions = ["created", "ready"],
  fetchExternalMetadata,
  parseUploadedGpx,
}) {
  const v = value || {};
  const set = (key, nextValue) => onChange({ ...v, [key]: nextValue });
  const [isParsingGPX, setIsParsingGPX] = useState(false);
  const [isFetchingExternalMetadata, setIsFetchingExternalMetadata] =
    useState(false);
  const [lastGPXFile, setLastGPXFile] = useState("");
  const [galleryDraft, setGalleryDraft] = useState(() =>
    (value?.gallery || []).join(", "),
  );
  const [manualMetricsOverride, setManualMetricsOverride] = useState(false);
  const hasGpxPoints = (v.gpx_points || []).length > 1;

  useEffect(() => {
    setGalleryDraft((v.gallery || []).join(", "));
  }, [v.gallery]);

  const maybeFetchExternalMetadata = async (rawURL) => {
    const trimmed = String(rawURL || "").trim();
    if (!trimmed || !fetchExternalMetadata) return;

    let parsed;
    try {
      parsed = new URL(trimmed);
    } catch {
      return;
    }

    const host = parsed.hostname.toLowerCase();
    const isBikepacking =
      host === "bikepacking.com" || host.endsWith(".bikepacking.com");
    if (!isBikepacking) return;

    setIsFetchingExternalMetadata(true);
    try {
      const data = await fetchExternalMetadata(trimmed);
      const next = {
        ...v,
        external_url: trimmed,
        name: data?.name || v.name,
        description: data?.description || v.description,
        distance_km:
          typeof data?.distance_km === "number" && data.distance_km > 0
            ? data.distance_km
            : v.distance_km,
        elevation_m:
          typeof data?.elevation_m === "number" && data.elevation_m > 0
            ? data.elevation_m
            : v.elevation_m,
        hero_image: data?.hero_image || v.hero_image,
        gallery:
          Array.isArray(data?.gallery) && data.gallery.length > 0
            ? data.gallery
            : v.gallery,
      };
      if (Array.isArray(data?.gpx_points) && data.gpx_points.length > 1) {
        next.gpx_points = data.gpx_points;
        setManualMetricsOverride(false);
      }
      onChange(next);
      const gpxMessage =
        Array.isArray(data?.gpx_points) && data.gpx_points.length > 1
          ? " and GPX track"
          : "";
      toast.success(`Imported route details${gpxMessage} from bikepacking.com`);
    } catch {
      toast.error("Could not import details from bikepacking.com URL");
    } finally {
      setIsFetchingExternalMetadata(false);
    }
  };

  const onGPXUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !parseUploadedGpx) return;

    setIsParsingGPX(true);
    try {
      const data = await parseUploadedGpx(file);
      onChange({
        ...v,
        gpx_points: data?.gpx_points || [],
        distance_km:
          typeof data?.distance_km === "number"
            ? data.distance_km
            : v.distance_km,
        elevation_m:
          typeof data?.elevation_m === "number"
            ? data.elevation_m
            : v.elevation_m,
      });
      setManualMetricsOverride(false);
      setLastGPXFile(file.name);
      toast.success("GPX imported");
    } catch {
      toast.error("Failed to parse GPX file");
    } finally {
      setIsParsingGPX(false);
      event.target.value = "";
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <Label>Name</Label>
        <Input
          data-testid="route-form-name"
          value={v.name || ""}
          onChange={(event) => set("name", event.target.value)}
        />
      </div>
      <div>
        <Label>Location</Label>
        <Input
          value={v.location || ""}
          onChange={(event) => set("location", event.target.value)}
        />
      </div>
      <div>
        <Label>Country</Label>
        <Input
          value={v.country || ""}
          onChange={(event) => set("country", event.target.value)}
        />
      </div>
      <div className="col-span-2">
        <Label>Description</Label>
        <Textarea
          value={v.description || ""}
          onChange={(event) => set("description", event.target.value)}
        />
      </div>
      <div>
        <Label>Status</Label>
        <Select
          value={String(v.status || "").trim() || "created"}
          onValueChange={(nextValue) => set("status", nextValue)}
        >
          <SelectTrigger data-testid="route-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Label>External URL (optional)</Label>
        <Input
          type="url"
          placeholder="https://example.com/route-guide"
          value={v.external_url || ""}
          onChange={(event) => set("external_url", event.target.value)}
          onBlur={(event) => {
            maybeFetchExternalMetadata(event.target.value);
          }}
        />
        <div className="text-xs text-[#5C665F] mt-2">
          {isFetchingExternalMetadata
            ? "Fetching route details from bikepacking.com..."
            : "Tip: paste a bikepacking.com route URL to auto-fill name, description, distance, climbing, images, and GPX track."}
        </div>
      </div>
      <div>
        <Label>Distance (km)</Label>
        <Input
          type="number"
          value={v.distance_km ?? 0}
          onChange={(event) =>
            set("distance_km", parseFloat(event.target.value) || 0)
          }
          disabled={hasGpxPoints && !manualMetricsOverride}
        />
      </div>
      <div>
        <Label>Elevation (m)</Label>
        <Input
          type="number"
          value={v.elevation_m ?? 0}
          onChange={(event) =>
            set("elevation_m", parseInt(event.target.value, 10) || 0)
          }
          disabled={hasGpxPoints && !manualMetricsOverride}
        />
      </div>
      <div className="col-span-2 flex items-center justify-between rounded-md border border-[#E5E0D8] bg-[#FAF7F2] px-3 py-2">
        <div>
          <div className="text-sm font-medium">Manual metrics override</div>
          <div className="text-xs text-[#5C665F]">
            {hasGpxPoints
              ? "Distance and climbing are auto-calculated from GPX unless enabled."
              : "Upload GPX data to auto-calculate distance and climbing."}
          </div>
        </div>
        <Switch
          checked={manualMetricsOverride}
          onCheckedChange={setManualMetricsOverride}
          disabled={!hasGpxPoints}
          aria-label="Toggle manual route metrics override"
        />
      </div>
      <div>
        <Label>Days</Label>
        <Input
          type="number"
          value={v.days ?? 1}
          onChange={(event) => set("days", parseInt(event.target.value, 10) || 1)}
        />
      </div>
      <div>
        <Label>Difficulty</Label>
        <Select
          value={v.difficulty || ""}
          onValueChange={(nextValue) => set("difficulty", nextValue)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["Easy", "Moderate", "Hard", "Expert"].map((difficulty) => (
              <SelectItem key={difficulty} value={difficulty}>
                {difficulty}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Label>Hero image URL</Label>
        <Input
          value={v.hero_image || ""}
          onChange={(event) => set("hero_image", event.target.value)}
        />
        {v.hero_image && (
          <div className="mt-2 relative">
            <img
              src={v.hero_image}
              alt="Hero preview"
              className="rounded-md max-h-48 w-full object-cover border border-border"
              onError={(event) => {
                event.target.style.display = "none";
              }}
              onLoad={(event) => {
                event.target.style.display = "";
              }}
            />
            <button
              type="button"
              aria-label="Remove hero image"
              className="absolute right-2 top-2 h-7 w-7 rounded-full bg-black/70 text-white text-lg leading-none hover:bg-black/85"
              onClick={() => set("hero_image", "")}
            >
              ×
            </button>
          </div>
        )}
      </div>
      <div className="col-span-2">
        <Label>Gallery URLs (comma separated)</Label>
        <Input
          value={galleryDraft}
          onChange={(event) => setGalleryDraft(event.target.value)}
          onBlur={() => {
            const urls = galleryDraft
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean);
            set("gallery", urls);
            setGalleryDraft(urls.join(", "));
          }}
        />
        {(v.gallery || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {v.gallery.map((url, index) => (
              <div key={index} className="relative">
                <img
                  src={url}
                  alt={`Gallery ${index + 1}`}
                  className="rounded-md h-20 w-28 object-cover border border-border"
                  onError={(event) => {
                    event.target.style.display = "none";
                  }}
                  onLoad={(event) => {
                    event.target.style.display = "";
                  }}
                />
                <button
                  type="button"
                  aria-label={`Remove gallery image ${index + 1}`}
                  className="absolute right-1 top-1 h-5 w-5 rounded-full bg-black/70 text-white text-sm leading-none hover:bg-black/85"
                  onClick={() => {
                    const next = [...(v.gallery || [])];
                    next.splice(index, 1);
                    set("gallery", next);
                    setGalleryDraft(next.join(", "));
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-2">
        <Label>Upload Route GPX file</Label>
        <Input
          type="file"
          accept=".gpx,application/gpx+xml,application/xml,text/xml"
          onChange={onGPXUpload}
          disabled={isParsingGPX}
          data-testid="route-form-gpx-upload"
        />
        <div className="text-xs text-[#5C665F] mt-2">
          {isParsingGPX
            ? "Parsing GPX file..."
            : `Loaded points: ${(v.gpx_points || []).length}${lastGPXFile ? ` (last file: ${lastGPXFile})` : ""}`}
        </div>
      </div>
      <PoiEditor
        className="col-span-2"
        title="Route map & POIs"
        gpxPoints={v.gpx_points || []}
        pois={v.pois || []}
        onChange={(nextPois) => set("pois", nextPois)}
      />
    </div>
  );
}